import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Settings } from "react-feather";
import { IconActionMenu, type IconActionMenuItem } from "./icon-action-menu";
import "./icon-action-menu-trigger.scss";

export interface IconActionMenuTriggerProps {
  /** Menu items shown when the trigger is clicked. */
  items: IconActionMenuItem[];
  /** Optional callback when an item is selected (after item.onClick). */
  onItemClick?: (item: IconActionMenuItem) => void;
  /** Optional callback when the menu opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** Size of the gear icon in pixels. Default: 24. */
  iconSize?: number;
  /** Additional class for the trigger button. */
  triggerClassName?: string;
  /** Additional class for the root wrapper. */
  className?: string;
  /** IconActionMenu props passed through (padding, gap, iconSize for menu items). */
  menuProps?: Partial<{
    padding: string;
    gap: string;
    iconSize: number;
  }>;
  /** Portal target (default: document.body). Menu is rendered here to avoid overflow clipping (e.g. in tables). */
  getPortalContainer?: () => HTMLElement;
}

const DEFAULT_ICON_SIZE = 24;
const MENU_OFFSET = 6;
const MENU_APPROX_WIDTH = 140;
const MENU_APPROX_HEIGHT = 120;

export function IconActionMenuTrigger({
  items,
  onItemClick,
  onOpenChange,
  iconSize = DEFAULT_ICON_SIZE,
  triggerClassName = "",
  className = "",
  menuProps,
  getPortalContainer,
}: IconActionMenuTriggerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    bottom: boolean;
    leftAlign: boolean;
  } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceRight = window.innerWidth - rect.right;
    const bottom = spaceBelow >= MENU_APPROX_HEIGHT || rect.top < spaceBelow;
    const leftAlign = spaceRight >= MENU_APPROX_WIDTH;
    setPosition({
      top: bottom ? rect.bottom + MENU_OFFSET : rect.top - MENU_OFFSET,
      left: leftAlign ? rect.left : rect.right,
      bottom,
      leftAlign,
    });
  };

  useEffect(() => {
    if (!isOpen || !triggerRef.current) {
      setPosition(null);
      return;
    }
    const onScrollOrResize = () => updatePosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (isOpen && triggerRef.current) {
      updatePosition();
    } else {
      setPosition(null);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        triggerRef.current &&
        menuRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        onOpenChange?.(false);
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onOpenChange]);

  const handleTriggerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isOpen;
    setIsOpen(next);
    onOpenChange?.(next);
  };

  const handleMenuItemClick = (item: IconActionMenuItem) => {
    onItemClick?.(item);
    setIsOpen(false);
    onOpenChange?.(false);
  };

  const portalTarget = getPortalContainer?.() ?? document.body;

  const dropdownContent =
    isOpen && position ? (
      <div
        ref={menuRef}
        id="icon-action-menu-dropdown"
        role="presentation"
        className="icon-action-menu-trigger__dropdown icon-action-menu-trigger__dropdown--portal"
        style={{
          position: "fixed",
          top: position.bottom ? position.top : undefined,
          bottom: position.bottom ? undefined : `calc(100vh - ${position.top}px)`,
          left: position.leftAlign ? position.left : undefined,
          right: position.leftAlign ? undefined : `calc(100vw - ${position.left}px)`,
          minWidth: 121,
        }}
      >
        <IconActionMenu
          items={items}
          onItemClick={handleMenuItemClick}
          {...menuProps}
        />
      </div>
    ) : null;

  return (
    <div className={`icon-action-menu-trigger ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className={`icon-action-menu-trigger__button ${triggerClassName}`.trim()}
        onClick={handleTriggerClick}
        aria-label="Open menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? "icon-action-menu-dropdown" : undefined}
        style={{ ["--icon-action-menu-trigger-size" as string]: `${iconSize}px` }}
      >
        <Settings size={iconSize} strokeWidth={2} />
      </button>
      {dropdownContent != null && createPortal(dropdownContent, portalTarget)}
    </div>
  );
}
